import { trackLinkClick } from 'utils/tracking';
import { m } from 'motion/react';
import { ElementType } from 'react';
import {
  containerVariants,
  itemFadeIn,
  itemVariants,
  viewPort,
} from '../../../../utils/framer';
import KIAImage from '../../../components/ui/image/KIAImage';
import {
  Page_Pagebuilder_Blocks_ColumnsBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_ButtonBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_SpacerBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_TextBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_VideoBlock,
} from '../../../graphql/generated/graphql';
import Wysiwyg from '../../components/dom-parser';

const TextBlock = ({
  editor,
}: Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_TextBlock) => {
  if (!editor) return null;
  return <Wysiwyg variants={itemVariants}>{editor}</Wysiwyg>;
};

const ImageBlock = ({ image, focalX, focalY }: any) => {
  if (!image?.sourceUrl) return null;
  return (
    <m.div variants={itemFadeIn}>
      <KIAImage
        src={image.sourceUrl}
        alt={image.altText || ''}
        className="w-full h-auto object-cover shadow-sm"
        style={{
          objectPosition: `${focalX ?? 50}% ${focalY ?? 50}%`,
        }}
      />
    </m.div>
  );
};

const SpacerBlock = ({
  height,
}: Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_SpacerBlock) => {
  return <div style={{ height: `${height || 50}px` }} aria-hidden="true" />;
};

const VideoBlock = ({
  videoFile,
  posterImage,
  autoplay,
}: Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_VideoBlock) => {
  if (!videoFile?.sourceUrl) return null;

  return (
    <div className="relative w-full rounded-lg overflow-hidden shadow-sm">
      <video
        className="w-full h-auto block"
        poster={posterImage?.sourceUrl}
        controls={!autoplay}
        muted={autoplay}
        loop={autoplay}
        playsInline
        autoPlay={autoplay}
      >
        <source src={videoFile.sourceUrl} type={videoFile.mimeType} />
      </video>
    </div>
  );
};

const ButtonBlock = ({
  link,
  style,
  alignment,
}: Page_Pagebuilder_Blocks_ColumnsBlock_Columns_Content_ButtonBlock) => {
  if (!link?.url) return null;

  const stylesMap: Record<string, string> = {
    'Primary (Solid)':
      'bg-black text-white no-underline h-12 max-w-72 w-full grid place-content-center w-fit px-2 ',
    'Secondary (Outline)':
      'bg-transparent text-black no-underline h-12  max-w-72 w-full grid place-content-center w-fit px-2 border-2 border-black ',
    'Text Only': 'text-black',
  };

  const aligmentMap: Record<string, string> = {
    Bottom: 'mt-auto',
    Auto: '',
  };
  const btnClass = stylesMap[style] || stylesMap.primary;

  return (
    <m.a
      href={link.url}
      onClick={trackLinkClick}
      variants={itemVariants}
      target={link.target || '_self'}
      className={`transition-colors duration-200 hover:underline underline-offset-2 ${btnClass} ${aligmentMap[alignment]}`}
    >
      {link.title || 'Learn More'}
    </m.a>
  );
};

const CONTENT_COMPONENTS: Record<string, ElementType> = {
  Page_Pagebuilder_Blocks_ColumnsBlock_columns_Content_TextBlock: TextBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_columns_Content_ImageBlock: ImageBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_columns_Content_ButtonBlock: ButtonBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_columns_Content_VideoBlock: VideoBlock,
  Page_Pagebuilder_Blocks_ColumnsBlock_columns_Content_SpacerBlock: SpacerBlock,
};

const widthMap: Record<string, string> = {
  '100% (1/1)': 'col-span-12',
  '50% (1/2)': 'col-span-12 md:col-span-6',
  '33% (1/3)': 'col-span-12 md:col-span-4',
  '66% (2/3)': 'col-span-12 md:col-span-8',
  '25% (1/4)': 'col-span-12 md:col-span-3',
  '75% (3/4)': 'col-span-12 md:col-span-9',
};

export default function Columns({
  columns,
}: Page_Pagebuilder_Blocks_ColumnsBlock) {
  if (!columns || columns.length === 0) return null;
  return (
    <section className="w-wrapper-columns mx-auto my-32">
      <m.div
        initial="hidden"
        whileInView="visible"
        variants={containerVariants}
        viewport={viewPort}
        className="md:grid md:grid-cols-12 gap-10"
      >
        {columns.map((col, colIndex) => {
          const spanClass = widthMap[col?.width || '12'];

          return (
            <div
              key={colIndex}
              className={`${spanClass} first:mb-6 md:first:mb-0  flex flex-col gap-4`}
            >
              {col?.content?.map((block, blockIndex) => {
                if (!block) return null;

                const Component = CONTENT_COMPONENTS[block.__typename];

                if (Component) {
                  return <Component key={blockIndex} {...block} />;
                }

                return null;
              })}
            </div>
          );
        })}
      </m.div>
    </section>
  );
}
